/**
 * AI Analyst Hub — Central page listing all 14 module analysts as cards.
 *
 * Click a card → opens that module's full AIAnalystPanel (with chat).
 * Cards show last-generated timestamp and status pill if a report exists.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { Sparkles, ArrowLeft, Calendar, DollarSign, Loader2, Search, Crown, ChevronRight, CheckSquare, Square, Play, X } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_META = {
  SCHEDULE: { label: 'Schedule Management', icon: Calendar, color: '#3b82f6' },
  COST:     { label: 'Cost Management',     icon: DollarSign, color: '#10b981' },
};

export default function AIAnalystHub() {
  // ProjectContext exports `projectId` directly (NOT a `currentProject` object).
  const { projectId, projectName, loading: projectLoading } = useProject();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [latestMap, setLatestMap] = useState({}); // moduleKey → latest report
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  // ── Multi-select "Run All Selected" state ──
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({done: 0, total: 0, current: ''});
  const toggleKey = (k) => setSelectedKeys(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const selectAllVisible = (mods) => setSelectedKeys(new Set(mods.map(m => m.key)));
  const clearSelected = () => setSelectedKeys(new Set());

  const runOne = async (key) => {
    try {
      const r = await api.post(`/v1/projects/${projectId}/ai-analyst/${key}/analyze?force=true`, {}, { timeout: 30000 });
      if (r.data?.status === 'done') return r.data;
      const jobId = r.data?.jobId;
      if (!jobId) return r.data;
      for (let i = 0; i < 90; i++) {
        await new Promise(res => setTimeout(res, 3000));
        try {
          const s = await api.get(`/v1/projects/${projectId}/ai-analyst/${key}/status/${jobId}`, { timeout: 15000 });
          if (s.data?.status === 'done')   return s.data;
          if (s.data?.status === 'failed') throw new Error(s.data.error || 'Cognara run failed');
        } catch (pollErr) { /* keep polling on transient failure */ }
      }
      throw new Error('timeout');
    } catch (e) {
      return { key, error: e?.response?.data?.detail || e.message };
    }
  };

  const runAllSelected = async () => {
    const keys = Array.from(selectedKeys);
    if (!keys.length) { toast.info('Select at least one analyst (checkbox on any card).'); return; }
    setBatchRunning(true);
    setBatchProgress({done: 0, total: keys.length, current: keys[0]});
    // Run in parallel but throttled to 3 concurrent (avoid hammering LLM + Cloudflare)
    const CONCURRENCY = 3;
    let done = 0; const errors = [];
    const q = [...keys];
    const workers = Array.from({length: Math.min(CONCURRENCY, q.length)}, async () => {
      while (q.length) {
        const k = q.shift();
        setBatchProgress(p => ({ ...p, current: modules.find(m => m.key === k)?.label || k }));
        const r = await runOne(k);
        if (r?.error) errors.push(`${k}: ${r.error}`);
        done += 1;
        setBatchProgress(p => ({ ...p, done }));
      }
    });
    await Promise.all(workers);
    // Refresh latest map so status pills flip immediately
    try {
      const pairs = await Promise.all(keys.map(k =>
        api.get(`/v1/projects/${projectId}/ai-analyst/${k}/latest`)
          .then(r => [k, r.data]).catch(() => [k, null])
      ));
      setLatestMap(prev => { const m = {...prev}; pairs.forEach(([k, v]) => { if (v) m[k] = v; }); return m; });
    } catch {}
    setBatchRunning(false);
    setBatchProgress({done: 0, total: 0, current: ''});
    if (errors.length) toast.warning(`Ran ${keys.length - errors.length}/${keys.length} analysts — ${errors.length} failed`);
    else               toast.success(`Ran ${keys.length} analysts successfully`);
  };

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/v1/projects/${projectId}/ai-analyst/modules`)
      .then(r => setModules(r.data?.modules || []))
      .catch(() => setModules([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  // Best-effort: pull latest reports for status pill on each card
  useEffect(() => {
    if (!projectId || modules.length === 0) return;
    Promise.all(modules.map(m =>
      api.get(`/v1/projects/${projectId}/ai-analyst/${m.key}/latest`)
        .then(r => [m.key, r.data])
        .catch(() => [m.key, null])
    )).then(pairs => {
      const map = {};
      pairs.forEach(([k, v]) => { if (v) map[k] = v; });
      setLatestMap(map);
    });
  }, [projectId, modules]);

  const filtered = useMemo(() => {
    if (!search.trim()) return modules;
    const q = search.toLowerCase();
    return modules.filter(m =>
      m.label.toLowerCase().includes(q) ||
      m.focus.toLowerCase().includes(q) ||
      m.category.toLowerCase().includes(q));
  }, [modules, search]);

  const grouped = useMemo(() => {
    const by = { SCHEDULE: [], COST: [] };
    for (const m of filtered) (by[m.category] = by[m.category] || []).push(m);
    return by;
  }, [filtered]);

  if (selected) {
    return (
      <DashboardLayout>
        <div className="space-y-3 max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => setSelected(null)}
                  className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
                  data-testid="ai-hub-back">
            <ArrowLeft className="w-3 h-3" /> Back to all analysts
          </button>
          <AIAnalystPanel projectId={projectId} moduleKey={selected.key} defaultOpen={true} compact={false} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4" data-testid="ai-analyst-hub">
        {/* Header */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>AI Analyst Hub</h1>
            </div>
            <p className="text-sm opacity-70" style={{ color: 'var(--text-secondary)' }}>
              Module-specific analysts powered by Cognara — schedule performance,
              insights, critical activities, gaps, and catch-up plans.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded"
               style={{ background: 'var(--bg-input)', border: '1px solid var(--border-color)' }}>
            <Search className="w-3 h-3 opacity-60" />
            <input value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Search analysts…"
                   className="bg-transparent text-xs outline-none w-48"
                   style={{ color: 'var(--text-primary)' }}
                   data-testid="ai-hub-search" />
          </div>
        </div>

        {/* ── Multi-select action bar — visible when ≥1 analyst is ticked ── */}
        {projectId && selectedKeys.size > 0 && (
          <div className="flex items-center gap-3 rounded-xl p-2 sticky top-14 z-10"
               style={{ background: 'linear-gradient(90deg, #06b6d422, #3b82f622)',
                        border: '1px solid #06b6d466', backdropFilter: 'blur(8px)' }}
               data-testid="ai-hub-batch-bar">
            <span className="text-[11px] font-bold px-2 py-1 rounded"
                  style={{ background: '#06b6d433', color: '#06b6d4' }}>
              {selectedKeys.size} analyst{selectedKeys.size === 1 ? '' : 's'} selected
            </span>
            {batchRunning ? (
              <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                <Loader2 className="w-3 h-3 animate-spin" />
                Running {batchProgress.done}/{batchProgress.total} —
                <span className="font-semibold">{batchProgress.current}</span>
              </div>
            ) : (
              <>
                <button onClick={runAllSelected}
                        className="flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-semibold hover:opacity-90"
                        style={{ background: '#06b6d4', color: 'white' }}
                        data-testid="ai-hub-run-selected">
                  <Play className="w-3 h-3" /> Run All Selected
                </button>
                <button onClick={() => selectAllVisible(filtered)}
                        className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] hover:opacity-90"
                        style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                        data-testid="ai-hub-select-all">
                  <CheckSquare className="w-3 h-3" /> Select all visible
                </button>
                <button onClick={clearSelected}
                        className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] hover:opacity-90 ml-auto"
                        style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}
                        data-testid="ai-hub-clear">
                  <X className="w-3 h-3" /> Clear
                </button>
              </>
            )}
          </div>
        )}

        {!projectId && !projectLoading && (
          <div className="rounded-xl p-8 text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
               data-testid="ai-hub-no-project">
            <Sparkles className="w-8 h-8 mx-auto opacity-50 text-cyan-400" />
            <div className="mt-3 font-semibold" style={{ color: 'var(--text-primary)' }}>Pick a project first</div>
            <div className="text-xs opacity-70 mt-1" style={{ color: 'var(--text-secondary)' }}>
              The AI analysts are project-scoped. Open a project from the Portfolio dashboard, then return here.
            </div>
          </div>
        )}

        {/* CEO BRIEFING — macro card that aggregates all 14 analysts */}
        {projectId && (
          <button onClick={() => navigate('/ceo-briefing')}
                  className="w-full rounded-xl p-4 text-left transition-transform hover:scale-[1.005] hover:shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #f59e0b15, #ef444411)',
                           border: '1px solid #f59e0b55' }}
                  data-testid="ai-hub-ceo-briefing-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                   style={{ background: '#f59e0b22', border: '1px solid #f59e0b66' }}>
                <Crown className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>CEO Briefing</div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                        style={{ background: '#f59e0b22', color: '#f59e0b' }}>MACRO</span>
                </div>
                <div className="text-[11px] opacity-70 mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                  One-page executive summary aggregating all 14 analysts → Top-3 risks ranked, owner interventions, 30-day priorities.
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-amber-400" />
            </div>
          </button>
        )}

        {loading && (
          <div className="py-10 text-center opacity-60">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            <div className="text-xs mt-2">Loading analyst catalogue…</div>
          </div>
        )}

        {!loading && Object.entries(grouped).map(([category, mods]) => {
          if (!mods.length) return null;
          const meta = CATEGORY_META[category] || { label: category, icon: Sparkles, color: '#06b6d4' };
          const Icon = meta.icon;
          return (
            <div key={category} className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <Icon className="w-4 h-4" style={{ color: meta.color }} />
                <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{meta.label}</div>
                <div className="text-[10px] opacity-50">{mods.length} analysts</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {mods.map(m => {
                  const latest = latestMap[m.key];
                  const status = latest?.report?.[m.domain === 'cost' ? 'cost_performance' : 'schedule_performance']?.status;
                  const pillColor = status === 'GREEN' ? '#10b981' : status === 'AMBER' ? '#f59e0b' : status === 'RED' ? '#ef4444' : '#1f2937';
                  const isPicked = selectedKeys.has(m.key);
                  return (
                    <div key={m.key}
                         className="relative rounded-xl p-3 text-left transition-transform hover:scale-[1.01] hover:shadow-lg cursor-pointer"
                         onClick={() => setSelected(m)}
                         style={{ background: 'var(--card-bg)',
                                  border: isPicked ? '2px solid #06b6d4' : '1px solid var(--border-color)',
                                  boxShadow: isPicked ? '0 0 0 3px rgba(6,182,212,0.15)' : undefined }}
                         data-testid={`ai-hub-card-${m.key}`}>
                      {/* Multi-select checkbox — stops propagation so ticking does not open the panel */}
                      <button onClick={(e) => { e.stopPropagation(); toggleKey(m.key); }}
                              className="absolute top-2 right-2 p-1 rounded hover:opacity-80"
                              style={{ background: isPicked ? '#06b6d433' : 'var(--surface-bg)' }}
                              title={isPicked ? 'Unselect' : 'Select for batch run'}
                              data-testid={`ai-hub-pick-${m.key}`}>
                        {isPicked ? <CheckSquare className="w-3.5 h-3.5" style={{ color: '#06b6d4' }} />
                                  : <Square      className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />}
                      </button>
                      <div className="flex items-start gap-2 mb-2 pr-7">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                             style={{ background: `${m.iconColor}22`, border: `1px solid ${m.iconColor}44` }}>
                          <Sparkles className="w-4 h-4" style={{ color: m.iconColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{m.label}</div>
                          <div className="text-[10px] opacity-60 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                            {m.focus.slice(0, 110)}{m.focus.length > 110 ? '…' : ''}
                          </div>
                        </div>
                        {status && (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold ml-1"
                                style={{ background: `${pillColor}22`, color: pillColor }}>
                            {status}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-[10px] opacity-60">
                        <span>{latest ? `Updated ${new Date(latest.generatedAt).toLocaleDateString()}` : 'No analysis yet'}</span>
                        <span className="text-cyan-400 font-medium">Open →</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
